import { Fragment, useState, useRef, useCallback, useEffect } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { Users } from './';
import { KeenIcon, Modal } from '@/components';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { usePermissions } from '@/hooks/usePermissions';
import clsx from 'clsx';
import { phoneNumberValidation } from '@/utils/validations';
import { Statuses } from '@/constant';
import { useCreateUserMutation } from '@/services/UserManagementService';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import { NationalitySelect, CountrySelect } from '@/components/CountryCodeSelect';
import { ErrorApiResponse, ModalProps } from '@/types/api.types';
import { createUserValidationSchema } from '@/utils/userValidations';
import { RolesService } from '@/services/roles/rolesService';

const SafeModal = ({ ...props }: ModalProps) => {
  return <Modal {...props} />;
};

export interface CreateUserForm {
  userType: string[];
  name: string;
  countryCode: string;
  countryShortCode: string;
  phoneCountryShortCode: string;
  userCountryCode: string;
  phoneNumber: string;
  nationality: string;
  email: string;
  profilePicture: File | null;
  linkedRegions: string[];
}

const UserManagement = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [roleOptions, setRoleOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [formData, setFormData] = useState<CreateUserForm>({
    userType: [],
    name: '',
    countryCode: '+1',
    countryShortCode: 'US',
    phoneCountryShortCode: 'US',
    userCountryCode: '',
    phoneNumber: '',
    nationality: '',
    email: '',
    profilePicture: null,
    linkedRegions: [],
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateUserForm, string>>
  >({});
  const { enqueueSnackbar } = useSnackbar();
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevPhoneErrorRef = useRef<string | undefined>(errors.phoneNumber);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validatePhoneNumber = useCallback(
    async (
      phone: string,
      shortCode: string,
      field: 'phoneNumber' | 'contactPhone'
    ) => {
      try {
        const schema = Yup.object().shape({
          [field]: phoneNumberValidation(
            field === 'phoneNumber' ? 'Phone number' : 'Contact phone',
            field === 'contactPhone',
            'phoneCountryShortCode'
          ),
        });

        const tempFormData = {
          [field]: phone,
          phoneCountryShortCode: shortCode,
        };

        await schema.validateAt(field, tempFormData, {
          abortEarly: true,
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const newError = err.errors[0];
          if (prevPhoneErrorRef.current !== newError) {
            setErrors((prev) => {
              if (prev.phoneNumber === newError) return prev;
              return { ...prev, phoneNumber: newError };
            });
            prevPhoneErrorRef.current = newError;
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    const activeShortCode = formData.phoneCountryShortCode || formData.countryShortCode;
    if (activeShortCode && isCreateModalOpen) {
      if (formData.phoneNumber) {
        validatePhoneNumber(
          formData.phoneNumber,
          activeShortCode,
          'phoneNumber'
        );
      }
    }
  }, [
    formData.phoneNumber,
    formData.countryShortCode,
    formData.phoneCountryShortCode,
    formData.userType,
    validatePhoneNumber,
    errors.phoneNumber,
  ]);

  useEffect(() => {
    const fetchRoleOptions = async () => {
      try {
        const options = await RolesService.getRoleOptions();
        setRoleOptions(options);
      } catch (error) {
        console.error('Failed to fetch role options:', error);
        // Fallback to empty array or show error
        setRoleOptions([]);
      }
    };

    fetchRoleOptions();
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
    setErrors({});
    if (process.env.NODE_ENV === 'test') {
      inputRef.current?.focus();
    } else {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsRoleDropdownOpen(false);
    setFormData({
      userType: [],
      name: '',
      countryCode: '+1',
      countryShortCode: 'US',
      phoneCountryShortCode: 'US',
      userCountryCode: '',
      phoneNumber: '',
      nationality: '',
      email: '',
      profilePicture: null,
      linkedRegions: [],
    });
    setPreviewUrl(null);
    setErrors({});
  }, []);

  const validateField = useCallback(
    async (name: keyof CreateUserForm, value: CreateUserForm[typeof name]) => {
      try {
        await createUserValidationSchema.validateAt(name, {
          [name]: value,
          countryShortCode: formData.countryShortCode,
          phoneCountryShortCode: formData.phoneCountryShortCode,
          userType: formData.userType,
        });
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    [formData.countryShortCode, formData.phoneCountryShortCode, formData.userType]
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;
      setFormData((prev) => {
        if (prev[name as keyof CreateUserForm] === value) return prev;
        return {
          ...prev,
          [name]: value,
        };
      });
      validateField(name as keyof CreateUserForm, value);
    },
    [validateField]
  );

  const handleRoleChange = useCallback(
    (role: string) => {
      setFormData((prev) => {
        if (role === 'Boater' || role === 'Marina') {
          let newRoles = [...prev.userType];
          if (newRoles.includes(role)) {
            newRoles = newRoles.filter((r) => r !== role);
          } else {
            if (role === 'Boater') {
              newRoles = newRoles.filter((r) => r !== 'Marina');
            } else if (role === 'Marina') {
              newRoles = newRoles.filter((r) => r !== 'Boater');
            }
            newRoles.push(role);
          }

          return {
            ...prev,
            userType: newRoles,
          };
        } else {
          let newRoles = [...prev.userType];

          newRoles = newRoles.includes(role)
            ? newRoles.filter((r) => r !== role)
            : [...newRoles, role];

          return {
            ...prev,
            userType: newRoles,
          };
        }
      });
      validateField('userType', formData.userType);
    },
    [validateField]
  );

  const handleCountryCodeChange = useCallback(
    (code: string, shortCode: string) => {
      setFormData((prev) => {
        if (
          prev.countryCode === code &&
          prev.phoneCountryShortCode === shortCode
        ) {
          return prev;
        }
        return {
          ...prev,
          countryCode: code,
          phoneCountryShortCode: shortCode,
        };
      });
      validateField('countryCode', code);
      validateField('phoneCountryShortCode', shortCode);
      if (formData.phoneNumber) {
        validateField('phoneNumber', formData.phoneNumber);
      }
    },
    [validateField, formData.phoneNumber]
  );

  const handleUserCountryChange = useCallback(
    (_code: string, shortCode: string) => {
      setFormData((prev) => {
        return {
          ...prev,
          userCountryCode: shortCode,
          countryShortCode: shortCode,
        };
      });

      validateField(
        'countryShortCode',
        shortCode as CreateUserForm['countryShortCode']
      );
    },
    [validateField]
  );

  const handleNationalityChange = useCallback(
    (value: string) => {
      setFormData((prev) => {
        if (prev.nationality === value) return prev;
        return {
          ...prev,
          nationality: value,
        };
      });
      validateField('nationality', value);
    },
    [validateField]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));
      validateField('profilePicture', file);
      if (file) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    },
    [validateField]
  );

  const handleRemoveImage = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFormData((prev) => ({
      ...prev,
      profilePicture: null,
    }));
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    validateField('profilePicture', null);
  }, [previewUrl]);

  const handleBlur = useCallback(
    (name: keyof CreateUserForm) => {
      validateField(name, formData[name]);
    },
    [validateField, formData]
  );

  const handleSubmit = useCallback(async () => {
    try {
      await createUserValidationSchema.validate(formData, {
        abortEarly: false,
      });
      setErrors({});
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      formData.userType.forEach((type) => {
        payload.append('roles[]', type);
      });
      payload.append('status', String(Statuses.ACTIVE));
      payload.append('country_code', formData.countryCode);
      payload.append('country_short_code', formData.countryShortCode as string);
      payload.append('phone_country_short_code', (formData.phoneCountryShortCode || formData.countryShortCode) as string);
      payload.append('mobile', formData.phoneNumber);
      if (formData.nationality) {
        payload.append('nationality', formData.nationality);
      }
      if (formData.profilePicture) {
        payload.append('profile_picture', formData.profilePicture);
      }

      const isAdminPanel: boolean = formData.userType.some(
        (type) =>
          type === 'Admin' ||
          type === 'Yacht Club Admin' ||
          type === 'Region Operator' ||
          type === 'DMO Admin'
      );
      payload.append('isAdminPanel', isAdminPanel.toString());

      await createUser(payload).unwrap();
      enqueueSnackbar('User created successfully', { variant: 'success' });
      handleCloseCreateModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) {
            validationErrors[error.path] = error.message;
          }
        });
        console.error('validationErrors', validationErrors);
        setErrors(validationErrors);
        enqueueSnackbar('Please correct the form errors', { variant: 'error' });
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'Failed to create user';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  }, [createUser, enqueueSnackbar, formData, handleCloseCreateModal]);

  return (
    <Fragment>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle id="user-management-heading" />
          </ToolbarHeading>
          <ToolbarActions>
            {hasPermission('user_management_create') && (
              <button
                className="btn btn-sm btn-light"
                onClick={handleOpenCreateModal}
                aria-label="Add new user"
              >
                Add New User
              </button>
            )}
          </ToolbarActions>
        </Toolbar>
      </Container>
      <Container>
        <Users />
      </Container>
      <SafeModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        className="!flex items-center justify-center"
        data-testid="create-user-modal"
        aria-labelledby="create-user-modal-title"
      >
        <div className="bg-gray-100 p-6 w-full max-w-md mx-4 rounded-lg shadow-lg flex flex-col max-h-[97dvh] overflow-y-auto">
          <h1
            id="create-user-modal-title"
            className="text-lg font-medium text-gray-900 mb-4"
          >
            Create User
          </h1>
          <div
            tabIndex={0}
            role="region"
            aria-label="Create user form"
            className="flex-1 overflow-y-auto max-h-[70vh] custom-scrollbar focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <div className="flex flex-col gap-4 pr-3">
              <div>
                <label
                  htmlFor="name-input"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Name
                </label>
                <input
                  ref={inputRef}
                  id="name-input"
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('name')}
                  disabled={isCreating}
                  className={clsx(
                    'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                    isCreating ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-100',
                    errors.name && 'border-red-500 focus:ring-red-500'
                  )}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  placeholder="Enter name"
                  data-testid="name-input"
                />
                {errors.name && (
                  <p
                    id="name-error"
                    className="text-red-600 text-sm mt-1"
                    data-testid="name-error"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="email-input"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email
                </label>
                <input
                  id="email-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  disabled={isCreating}
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('email')}
                  className={clsx(
                    'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                    isCreating ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-100',
                    errors.email && 'border-red-500 focus:ring-red-500'
                  )}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  placeholder="Enter email"
                />
                {errors.email && (
                  <p id="email-error" className="text-red-600 text-sm mt-1">
                    {errors.email}
                  </p>
                )}
              </div>
              <div ref={roleDropdownRef}>
                <label
                  htmlFor="userType-input"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Roles
                </label>
                <div className="relative">
                  <button
                    type="button"
                    id="userType-input"
                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                    disabled={isCreating}
                    className={clsx(
                      'w-full h-10 border rounded-md px-3 py-2 text-left text-sm',
                      'border-gray-300 focus:ring-primary focus:border-primary',
                      isCreating ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-gray-100 text-gray-700',
                      'flex items-center justify-between',
                      errors.userType && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-expanded={isRoleDropdownOpen}
                    aria-controls="role-dropdown"
                    aria-invalid={!!errors.userType}
                    aria-describedby={
                      errors.userType ? 'userType-error' : undefined
                    }
                    data-testid="role-select-button"
                  >
                    <span className="flex items-center gap-1 overflow-hidden">
                      {formData.userType.length > 0 ? (
                        <span className="flex flex-wrap gap-1">
                          {formData.userType.slice(0, 2).map((role) => (
                            <span
                              key={role}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-white"
                            >
                              <span className="truncate max-w-[80px]">
                                {role}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRoleChange(role);
                                }}
                                className="ml-1 flex-shrink-0 focus:outline-none"
                                aria-label={`Remove ${role}`}
                              >
                                <KeenIcon
                                  type="filled"
                                  icon="cross"
                                  className="text-white w-3 h-3"
                                />
                              </button>
                            </span>
                          ))}
                          {formData.userType.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                              +{formData.userType.length - 2}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">
                          Select roles
                        </span>
                      )}
                    </span>
                    <KeenIcon
                      type="filled"
                      icon={isRoleDropdownOpen ? 'up' : 'down'}
                      className="text-gray-500 w-4 h-4"
                    />
                  </button>
                  {isRoleDropdownOpen && (
                    <div
                      id="role-dropdown"
                      className="absolute z-10 mt-2 w-full bg-gray-200 shadow-lg rounded-md border border-gray-200 max-h-48 overflow-y-auto"
                    >
                      {roleOptions.map((option, index) => (
                        <label
                          key={option.value}
                          className={clsx(
                            'flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer',
                            index === 0 && 'rounded-t-md',
                            index === roleOptions.length - 1 && 'rounded-b-md',
                            option.value === 'Marina' &&
                            formData.userType.includes('Boater') &&
                            'opacity-50',
                            option.value === 'Boater' &&
                            formData.userType.includes('Marina') &&
                            'opacity-50'
                          )}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRoleChange(option.value);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.userType.includes(option.value)}
                            onChange={() => handleRoleChange(option.value)}
                            disabled={
                              isCreating ||
                              (option.value === 'Marina' &&
                                formData.userType.includes('Boater')) ||
                              (option.value === 'Boater' &&
                                formData.userType.includes('Marina'))
                            }
                            className={clsx(
                              'h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer',
                              option.value === 'Boater' ? 'rounded-full' : ''
                            )}
                            aria-label={`Select ${option.label}`}
                          />
                          <span className="ml-2 text-sm text-gray-700 cursor-pointer">
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {errors.userType && (
                  <p
                    id="userType-error"
                    className="text-red-600 text-sm mt-1"
                    data-testid="userType-error"
                  >
                    {errors.userType}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <div className="w-1/3">
                  <label
                    htmlFor="countryCode-select"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Country Code
                  </label>
                  <CountryCodeSelect
                    id="countryCode-select"
                    className="w-full bg-gray-100 rounded-md"
                    selected={formData.phoneCountryShortCode || formData.countryShortCode}
                    setFieldValue={handleCountryCodeChange}
                    disabled={isCreating}
                    hasError={!!errors.phoneCountryShortCode || !!errors.countryCode}
                  />
                  {(errors.countryCode || errors.phoneCountryShortCode) && (
                    <p
                      id="countryCode-error"
                      className="text-red-600 text-sm mt-1"
                    >
                      {errors.countryCode || errors.phoneCountryShortCode}
                    </p>
                  )}
                </div>
                <div className="w-2/3">
                  <label
                    htmlFor="phoneNumber-input"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Phone Number
                  </label>
                  <input
                    id="phoneNumber-input"
                    type="tel"
                    name="phoneNumber"
                    autoComplete="tel"
                    value={formData.phoneNumber}
                    disabled={isCreating}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange({
                        target: { name: 'phoneNumber', value: digitsOnly },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    onBlur={() => handleBlur('phoneNumber')}
                    className={clsx(
                      'input input-bordered w-full h-10 text-sm border-gray-300 focus:ring-primary focus:border-primary',
                      isCreating ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-100',
                      errors.phoneNumber && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.phoneNumber}
                    aria-describedby={
                      errors.phoneNumber ? 'phoneNumber-error' : undefined
                    }
                    placeholder="Enter phone number"
                  />
                  {errors.phoneNumber && (
                    <p
                      id="phoneNumber-error"
                      className="text-red-600 text-sm mt-1"
                    >
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label
                  htmlFor="user-country-select"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Country
                </label>
                <CountrySelect
                  id="user-country-select"
                  className="w-full"
                  selected={formData.countryShortCode}
                  setFieldValue={handleUserCountryChange}
                  disabled={isCreating}
                  hasError={!!errors.countryShortCode}
                />
                {errors.countryShortCode && (
                  <p
                    id="user-country-error"
                    className="text-red-600 text-sm mt-1"
                  >
                    {errors.countryShortCode}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="nationality-input"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Nationality
                </label>
                <NationalitySelect
                  id="nationality-input"
                  selected={formData.nationality}
                  setFieldValue={handleNationalityChange}
                  disabled={isCreating}
                  hasError={!!errors.nationality}
                  className="w-full"
                />
                {errors.nationality && (
                  <p
                    id="nationality-error"
                    className="text-red-600 text-sm mt-1"
                  >
                    {errors.nationality}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="profilePicture-input"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Profile Picture (Optional)
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      id="profilePicture-input"
                      type="file"
                      name="profilePicture"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      onBlur={() => handleBlur('profilePicture')}
                      disabled={isCreating}
                      className="hidden"
                      aria-describedby={
                        errors.profilePicture
                          ? 'profilePicture-error'
                          : undefined
                      }
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isCreating}
                      className="btn btn-sm btn-light flex items-center justify-center gap-2 w-full sm:w-auto"
                      aria-label="Choose profile picture"
                    >
                      Choose File
                    </button>
                    {formData.profilePicture && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        disabled={isCreating}
                        className="btn btn-sm btn-light btn-icon"
                        aria-label="Remove profile picture"
                      >
                        <KeenIcon
                          type="filled"
                          icon="trash"
                          className="text-red-500"
                        />
                      </button>
                    )}
                  </div>
                  {previewUrl ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={previewUrl}
                        alt="Profile preview"
                        className="w-16 h-16 rounded-full object-cover border border-gray-300"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700">
                          {formData.profilePicture?.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formData.profilePicture?.size &&
                            (
                              formData.profilePicture?.size /
                              1024 /
                              1024
                            ).toFixed(2)}{' '}
                          MB
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center border border-gray-300">
                      <KeenIcon
                        type="filled"
                        icon="user"
                        className="text-gray-500 text-2xl"
                      />
                    </div>
                  )}
                  {errors.profilePicture && (
                    <p
                      id="profilePicture-error"
                      className="text-red-600 text-sm mt-1"
                    >
                      {errors.profilePicture}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCloseCreateModal}
              className="btn btn-sm btn-light"
              aria-label="Cancel"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isCreating}
              className="btn btn-sm btn-primary"
              aria-label="Create user"
            >
              Create
            </button>
          </div>
        </div>
      </SafeModal>
    </Fragment>
  );
};
export { UserManagement };
